import { useEffect, useState } from 'react';
import ValidatedInput, { ValidatedState } from '../custom-input/ValidatedInput';
import { Validators } from '../../types/enums/validators.enum';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';
import './locationModal.scss';

export type TankFormValues = {
  name: string;
  active: boolean;
  isTravelingTank: boolean;
  isMaintenanceEnabled?: boolean;
};

type TankModalProps = {
  mode: 'add' | 'edit';
  close: () => void;
  siteName: string;
  tank?: TankFormValues;
  code?: string;
  showMaintenanceToggle?: boolean;
  setSelectedId?: (id: number) => void;
  createTank?: (values: TankFormValues) => Promise<number>;
  updateTank?: (values: TankFormValues) => Promise<void>;
};

const TankModal = ({
  mode,
  close,
  siteName,
  tank,
  code,
  showMaintenanceToggle = false,
  setSelectedId,
  createTank,
  updateTank,
}: TankModalProps): JSX.Element => {
  const [nameInput, setNameInput] = useState<ValidatedState>({ valid: mode === 'edit', value: tank?.name ?? '' });
  const [activeInput, setActiveInput] = useState<boolean>(tank?.active ?? true);
  const [travelingInput, setTravelingInput] = useState<boolean>(tank?.isTravelingTank ?? false);
  const [maintenanceEnabledInput, setMaintenanceEnabledInput] = useState<boolean>(tank?.isMaintenanceEnabled ?? true);
  const [validate, setValidate] = useState<boolean>(false);
  const [submit, setSubmit] = useState<boolean>(false);

  useEffect(() => {
    const action = async () => {
      const values: TankFormValues = {
        name: nameInput.value.trim(),
        active: activeInput,
        isTravelingTank: travelingInput,
        ...(showMaintenanceToggle ? { isMaintenanceEnabled: maintenanceEnabledInput } : {}),
      };
      try {
        if (mode === 'add' && createTank) {
          const id = await createTank(values);
          setSelectedId?.(id);
          showToast.success(toastMessages.TANK_CREATED_SUCCESS);
        } else if (mode === 'edit' && updateTank) {
          await updateTank(values);
          showToast.success(toastMessages.TANK_UPDATE_SUCCESS);
        }
        close();
      } catch (error: any) {
        if (error?.suppressToast) {
          return;
        }
        showToast.error(
          error?.response?.data?.title || (mode === 'add' ? toastMessages.TANK_CREATED_FAIL : toastMessages.TANK_UPDATE_FAIL),
        );
      } finally {
        setSubmit(false);
      }
    };
    if (submit) {
      action();
    }
  }, [submit]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setValidate(true);
    if (nameInput.valid) {
      setSubmit(true);
    }
  };

  return (
    <div className="location-modal">
      <form onSubmit={handleSubmit}>
        <h2>{mode === 'add' ? LABEL_CONSTANTS.ADD_TANK : LABEL_CONSTANTS.EDIT_TANK}</h2>
        <hr />
        <p>{mode === 'add' ? LABEL_CONSTANTS.ADD_TANK_HEADER : LABEL_CONSTANTS.EDIT_TANK_HEADER}</p>
        <div className="form-row">
          <label>{LABEL_CONSTANTS.SITE_NAME}:</label>
          <div className="input-container">
            <label>{siteName}</label>
          </div>
        </div>
        {mode === 'edit' && code && (
          <div className="form-row">
            <label>{LABEL_CONSTANTS.TANK_CODE}:</label>
            <div className="input-container">
              <label className="location-code-value">{code}</label>
            </div>
          </div>
        )}
        <div className="validated-input-row">
          <ValidatedInput
            useId="tank-name-input"
            type="text"
            label={LABEL_CONSTANTS.TANK_NAME}
            validate={validate}
            validatedStateForAutoFill={nameInput}
            setValidatedState={setNameInput}
            validators={[Validators.REQUIRED]}
          />
        </div>
        <div className="form-row">
          <label htmlFor="tank-active-checkbox">{LABEL_CONSTANTS.ACTIVE}</label>
          <div className="input-container">
            <input
              id="tank-active-checkbox"
              type="checkbox"
              defaultChecked={activeInput}
              onChange={e => setActiveInput(e.target.checked)}
            />
          </div>
        </div>
        <div className="form-row">
          <label htmlFor="traveling-checkbox">{LABEL_CONSTANTS.TRAVELING_TANK}</label>
          <div className="input-container">
            <input
              id="traveling-checkbox"
              type="checkbox"
              defaultChecked={travelingInput}
              onChange={e => setTravelingInput(e.target.checked)}
            />
          </div>
        </div>
        {showMaintenanceToggle && (
          <div className="form-row">
            <label htmlFor="maintenance-enabled-checkbox">{LABEL_CONSTANTS.MAINTENANCE_TRACKING}</label>
            <div className="input-container">
              <input
                id="maintenance-enabled-checkbox"
                type="checkbox"
                defaultChecked={maintenanceEnabledInput}
                onChange={e => setMaintenanceEnabledInput(e.target.checked)}
              />
            </div>
          </div>
        )}
        <div className="flex-right submit-button location-modal-actions">
          <button type="button" className="small green inverted button" onClick={close}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          &emsp;
          <button type="submit" className={nameInput.valid ? 'small green button' : 'small green button disabled'}>
            {mode === 'add' ? BUTTON_CONSTANTS.SUBMIT : BUTTON_CONSTANTS.UPDATE}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TankModal;
